import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Building2,
  Users,
  DollarSign,
  TrendingUp,
  UserX,
  UserCheck,
  Crown,
  Activity,
  Hotel,
  Search,
  ChevronLeft,
  ChevronRight,
  Shield,
  ArrowLeft,
  Trash2,
  FileText,
  Filter,
  Pencil
} from 'lucide-react';
import { API_BASE } from '@/config/api';

const API = API_BASE;

export default function PlatformAdminPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [hotels, setHotels] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [hotelPage, setHotelPage] = useState(1);
  const [userPage, setUserPage] = useState(1);
  const [hotelPages, setHotelPages] = useState(0);
  const [userPages, setUserPages] = useState(0);
  const [hotelSearch, setHotelSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [planDialogUser, setPlanDialogUser] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('starter');
  const [selectedCycle, setSelectedCycle] = useState('monthly');
  
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteDialogUser, setDeleteDialogUser] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Audit logs state
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(1);
  const [auditFilter, setAuditFilter] = useState({ resource_type: '', action: '' });
  const [auditLoading, setAuditLoading] = useState(false);

  // Hotel management state
  const [editHotelDialogOpen, setEditHotelDialogOpen] = useState(false);
  const [deleteHotelDialogOpen, setDeleteHotelDialogOpen] = useState(false);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [editHotelData, setEditHotelData] = useState({ name: '', city: '', country: '', address: '' });
  const [hotelDeleting, setHotelDeleting] = useState(false);

  useEffect(() => {
    if (user && user.role !== 'super_admin') {
      toast.error('Access denied');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!user || user.role !== 'super_admin') return;
    
    const headers = { Authorization: `Bearer ${token}` };
    
    axios.get(`${API}/api/platform-admin/stats`, { headers })
      .then(res => setStats(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [user, token]);

  useEffect(() => {
    if (activeTab !== 'hotels' || !token) return;
    const headers = { Authorization: `Bearer ${token}` };
    const params = { page: hotelPage, limit: 10 };
    if (hotelSearch) params.search = hotelSearch;
    
    axios.get(`${API}/api/platform-admin/hotels`, { headers, params })
      .then(res => {
        setHotels(res.data.hotels || []);
        setHotelPages(res.data.pagination?.pages || 0);
      })
      .catch(console.error);
  }, [activeTab, hotelPage, hotelSearch, token]);

  useEffect(() => {
    if (activeTab !== 'users' || !token) return;
    const headers = { Authorization: `Bearer ${token}` };
    const params = { page: userPage, limit: 10 };
    if (userSearch) params.search = userSearch;
    
    axios.get(`${API}/api/platform-admin/users`, { headers, params })
      .then(res => {
        setUsers(res.data.users || []);
        setUserPages(res.data.pagination?.pages || 0);
      })
      .catch(console.error);
  }, [activeTab, userPage, userSearch, token]);

  useEffect(() => {
    if (activeTab !== 'audit-logs' || !token) return;
    setAuditLoading(true);
    const headers = { Authorization: `Bearer ${token}` };
    const params = { limit: 20, skip: (auditPage - 1) * 20 };
    if (auditFilter.resource_type) params.resource_type = auditFilter.resource_type;
    if (auditFilter.action) params.action = auditFilter.action;
    
    axios.get(`${API}/api/audit`, { headers, params })
      .then(res => {
        setAuditLogs(res.data.logs || []);
        setAuditTotal(res.data.total || 0);
      })
      .catch(console.error)
      .finally(() => setAuditLoading(false));
  }, [activeTab, auditPage, auditFilter, token]);

  const handleSuspend = async (userId, isSuspended) => {
    const headers = { Authorization: `Bearer ${token}` };
    try {
      await axios.put(`${API}/api/platform-admin/users/${userId}/suspend`, null, { headers, params: { suspend: !isSuspended } });
      toast.success(isSuspended ? 'User unsuspended' : 'User suspended');
      setActiveTab(''); 
      setTimeout(() => setActiveTab('users'), 100);
    } catch (e) { toast.error('Failed'); }
  };

  const handlePlanUpdate = async () => {
    const headers = { Authorization: `Bearer ${token}` };
    try {
      await axios.put(`${API}/api/platform-admin/users/${planDialogUser.id}/plan`, null, { headers, params: { plan: selectedPlan, billing_cycle: selectedCycle } });
      toast.success('Plan updated');
      setPlanDialogOpen(false);
      setActiveTab('');
      setTimeout(() => setActiveTab('users'), 100);
    } catch (e) { toast.error('Failed'); }
  };

  const handleDeleteUser = async () => {
    const headers = { Authorization: `Bearer ${token}` };
    setDeleting(true);
    try {
      await axios.delete(`${API}/api/platform-admin/users/${deleteDialogUser.id}`, { headers });
      toast.success('User deleted successfully');
      setDeleteDialogOpen(false);
      setDeleteDialogUser(null);
      // Refresh stats and users
      axios.get(`${API}/api/platform-admin/stats`, { headers }).then(res => setStats(res.data));
      setActiveTab('');
      setTimeout(() => setActiveTab('users'), 100);
    } catch (e) { 
      toast.error(e.response?.data?.detail || 'Failed to delete user'); 
    } finally {
      setDeleting(false);
    }
  };

  const handleEditHotel = async () => {
    const headers = { Authorization: `Bearer ${token}` };
    try {
      await axios.put(`${API}/api/platform-admin/hotels/${selectedHotel.id}`, null, { 
        headers, 
        params: editHotelData 
      });
      toast.success('Hotel updated successfully');
      setEditHotelDialogOpen(false);
      setSelectedHotel(null);
      // Refresh hotels
      setActiveTab('');
      setTimeout(() => setActiveTab('hotels'), 100);
    } catch (e) { 
      toast.error(e.response?.data?.detail || 'Failed to update hotel'); 
    }
  };

  const handleDeleteHotel = async () => {
    const headers = { Authorization: `Bearer ${token}` };
    setHotelDeleting(true);
    try {
      await axios.delete(`${API}/api/platform-admin/hotels/${selectedHotel.id}`, { headers });
      toast.success('Hotel deleted successfully');
      setDeleteHotelDialogOpen(false);
      setSelectedHotel(null);
      // Refresh stats and hotels
      axios.get(`${API}/api/platform-admin/stats`, { headers }).then(res => setStats(res.data));
      setActiveTab('');
      setTimeout(() => setActiveTab('hotels'), 100);
    } catch (e) { 
      toast.error(e.response?.data?.detail || 'Failed to delete hotel'); 
    } finally {
      setHotelDeleting(false);
    }
  };

  const openEditHotelDialog = (hotel) => {
    setSelectedHotel(hotel);
    setEditHotelData({
      name: hotel.name || '',
      city: hotel.city || '',
      country: hotel.country || '',
      address: hotel.address || ''
    });
    setEditHotelDialogOpen(true);
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-900 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" /></div>;
  }

  const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-950 to-slate-900 text-white border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="text-slate-300 hover:text-white hover:bg-slate-800">
              <ArrowLeft className="h-4 w-4 mr-2" />Back
            </Button>
            <Shield className="h-8 w-8 text-amber-400" />
            <div>
              <h1 className="text-2xl font-bold">Platform Admin</h1>
              <p className="text-slate-400 text-sm">Control Tower</p>
            </div>
          </div>
          <Badge className="bg-amber-500 text-white"><Crown className="h-3 w-3 mr-1" />Super Admin</Badge>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-slate-800 border-b border-slate-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 flex gap-8">
          <button onClick={() => setActiveTab('overview')} className={`flex items-center gap-2 py-4 border-b-2 ${activeTab === 'overview' ? 'border-blue-500 text-blue-400' : 'border-transparent text-slate-400 hover:text-slate-200'}`}>
            <Activity className="h-4 w-4" />Overview
          </button>
          <button onClick={() => setActiveTab('hotels')} className={`flex items-center gap-2 py-4 border-b-2 ${activeTab === 'hotels' ? 'border-blue-500 text-blue-400' : 'border-transparent text-slate-400 hover:text-slate-200'}`}>
            <Hotel className="h-4 w-4" />Hotels
          </button>
          <button onClick={() => setActiveTab('users')} className={`flex items-center gap-2 py-4 border-b-2 ${activeTab === 'users' ? 'border-blue-500 text-blue-400' : 'border-transparent text-slate-400 hover:text-slate-200'}`}>
            <Users className="h-4 w-4" />Users
          </button>
          <button onClick={() => setActiveTab('analytics')} className={`flex items-center gap-2 py-4 border-b-2 ${activeTab === 'analytics' ? 'border-blue-500 text-blue-400' : 'border-transparent text-slate-400 hover:text-slate-200'}`}>
            <TrendingUp className="h-4 w-4" />Analytics
          </button>
          <button onClick={() => setActiveTab('audit-logs')} className={`flex items-center gap-2 py-4 border-b-2 ${activeTab === 'audit-logs' ? 'border-blue-500 text-blue-400' : 'border-transparent text-slate-400 hover:text-slate-200'}`}>
            <FileText className="h-4 w-4" />Audit Logs
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {activeTab === 'overview' && stats && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="bg-slate-800 border-slate-700"><CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">MRR</p>
                    <p className="text-3xl font-bold text-green-400">{fmt(stats.revenue?.mrr || 0)}</p>
                    <p className="text-xs text-slate-500 mt-1">ARR: {fmt(stats.revenue?.arr || 0)}</p>
                  </div>
                  <div className="h-12 w-12 bg-green-900/50 rounded-full flex items-center justify-center"><DollarSign className="h-6 w-6 text-green-400" /></div>
                </div>
              </CardContent></Card>
              
              <Card className="bg-slate-800 border-slate-700"><CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">Total Users</p>
                    <p className="text-3xl font-bold text-white">{stats.users?.total || 0}</p>
                    <p className="text-xs text-slate-500 mt-1">{stats.users?.active || 0} active</p>
                  </div>
                  <div className="h-12 w-12 bg-blue-900/50 rounded-full flex items-center justify-center"><Users className="h-6 w-6 text-blue-400" /></div>
                </div>
              </CardContent></Card>

              <Card className="bg-slate-800 border-slate-700"><CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">Total Hotels</p>
                    <p className="text-3xl font-bold text-white">{stats.hotels?.total || 0}</p>
                    <p className="text-xs text-slate-500 mt-1">{stats.activity?.total_rooms || 0} rooms</p>
                  </div>
                  <div className="h-12 w-12 bg-purple-900/50 rounded-full flex items-center justify-center"><Building2 className="h-6 w-6 text-purple-400" /></div>
                </div>
              </CardContent></Card>

              <Card className="bg-slate-800 border-slate-700"><CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">Signups (30d)</p>
                    <p className="text-3xl font-bold text-white">{stats.activity?.recent_signups_30d || 0}</p>
                    <p className="text-xs text-slate-500 mt-1">{stats.activity?.total_reservations || 0} reservations</p>
                  </div>
                  <div className="h-12 w-12 bg-amber-900/50 rounded-full flex items-center justify-center"><TrendingUp className="h-6 w-6 text-amber-400" /></div>
                </div>
              </CardContent></Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader><CardTitle className="text-white">Subscription Plans</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <Badge className="bg-slate-600 text-slate-200">trial</Badge>
                      <span className="text-xl font-semibold text-white">{stats.subscriptions?.by_plan?.trial || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <Badge className="bg-blue-600 text-white">starter</Badge>
                      <span className="text-xl font-semibold text-white">{stats.subscriptions?.by_plan?.starter || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <Badge className="bg-purple-600 text-white">professional</Badge>
                      <span className="text-xl font-semibold text-white">{stats.subscriptions?.by_plan?.professional || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <Badge className="bg-amber-600 text-white">premium</Badge>
                      <span className="text-xl font-semibold text-white">{stats.subscriptions?.by_plan?.premium || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-800 border-slate-700">
                <CardHeader><CardTitle className="text-white">Quick Stats</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Active Subscriptions</span>
                      <span className="font-semibold text-white">{stats.subscriptions?.active || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Trial Users</span>
                      <span className="font-semibold text-white">{stats.subscriptions?.trial || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Suspended Users</span>
                      <span className="font-semibold text-red-400">{stats.users?.suspended || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'hotels' && (
          <div className="space-y-6">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input placeholder="Search hotels..." value={hotelSearch} onChange={e => { setHotelSearch(e.target.value); setHotelPage(1); }} className="pl-10 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500" />
            </div>
            <Card className="bg-slate-800 border-slate-700">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-900">
                    <tr>
                      <th className="text-left p-4 font-medium text-slate-300">Hotel</th>
                      <th className="text-left p-4 font-medium text-slate-300">Owner</th>
                      <th className="text-left p-4 font-medium text-slate-300">Plan</th>
                      <th className="text-left p-4 font-medium text-slate-300">Rooms</th>
                      <th className="text-left p-4 font-medium text-slate-300">Status</th>
                      <th className="text-left p-4 font-medium text-slate-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hotels.length === 0 ? (
                      <tr><td colSpan={6} className="text-center py-8 text-slate-500">No hotels found</td></tr>
                    ) : (
                      hotels.map(h => (
                        <tr key={h.id} className="border-t border-slate-700">
                          <td className="p-4"><div className="font-medium text-white">{h.name}</div><div className="text-xs text-slate-500">{h.city}, {h.country}</div></td>
                          <td className="p-4"><div className="text-sm text-slate-300">{h.owner?.full_name || '-'}</div><div className="text-xs text-slate-500">{h.owner?.email}</div></td>
                          <td className="p-4"><Badge className={h.subscription?.plan === 'premium' ? 'bg-amber-600 text-white' : h.subscription?.plan === 'professional' ? 'bg-purple-600 text-white' : h.subscription?.plan === 'starter' ? 'bg-blue-600 text-white' : 'bg-slate-600 text-slate-200'}>{h.subscription?.plan || 'trial'}</Badge></td>
                          <td className="p-4 text-slate-300">{h.stats?.rooms || 0}</td>
                          <td className="p-4">{h.owner?.is_suspended ? <Badge className="bg-red-600 text-white">Suspended</Badge> : <Badge className="bg-green-600 text-white">Active</Badge>}</td>
                          <td className="p-4">
                            <div className="flex gap-2">
                              <Button variant="outline" size="sm" onClick={() => openEditHotelDialog(h)} className="border-slate-600 text-slate-300 hover:bg-slate-700">
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => { setSelectedHotel(h); setDeleteHotelDialogOpen(true); }} className="border-red-600 text-red-400 hover:bg-red-900/50">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
            {hotelPages > 1 && (
              <div className="flex justify-center gap-2">
                <Button variant="outline" size="sm" disabled={hotelPage === 1} onClick={() => setHotelPage(p => p - 1)} className="border-slate-600 text-slate-300 hover:bg-slate-700"><ChevronLeft className="h-4 w-4" /></Button>
                <span className="py-2 px-3 text-sm text-slate-400">Page {hotelPage} of {hotelPages}</span>
                <Button variant="outline" size="sm" disabled={hotelPage === hotelPages} onClick={() => setHotelPage(p => p + 1)} className="border-slate-600 text-slate-300 hover:bg-slate-700"><ChevronRight className="h-4 w-4" /></Button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <div className="space-y-6">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input placeholder="Search users..." value={userSearch} onChange={e => { setUserSearch(e.target.value); setUserPage(1); }} className="pl-10 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500" />
            </div>
            <Card className="bg-slate-800 border-slate-700">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-900">
                    <tr>
                      <th className="text-left p-4 font-medium text-slate-300">User</th>
                      <th className="text-left p-4 font-medium text-slate-300">Role</th>
                      <th className="text-left p-4 font-medium text-slate-300">Hotel</th>
                      <th className="text-left p-4 font-medium text-slate-300">Plan</th>
                      <th className="text-left p-4 font-medium text-slate-300">Status</th>
                      <th className="text-left p-4 font-medium text-slate-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.length === 0 ? (
                      <tr><td colSpan={6} className="text-center py-8 text-slate-500">No users found</td></tr>
                    ) : (
                      users.map(u => (
                        <tr key={u.id} className="border-t border-slate-700">
                          <td className="p-4"><div className="font-medium text-white">{u.full_name || '-'}</div><div className="text-xs text-slate-500">{u.email}</div></td>
                          <td className="p-4"><Badge variant="outline" className="border-slate-500 text-slate-300">{u.role}</Badge></td>
                          <td className="p-4 text-slate-300">{u.hotel?.name || '-'}</td>
                          <td className="p-4"><Badge className={u.subscription?.plan === 'premium' ? 'bg-amber-600 text-white' : u.subscription?.plan === 'professional' ? 'bg-purple-600 text-white' : u.subscription?.plan === 'starter' ? 'bg-blue-600 text-white' : 'bg-slate-600 text-slate-200'}>{u.subscription?.plan || 'trial'}</Badge></td>
                          <td className="p-4">{u.is_suspended ? <Badge className="bg-red-600 text-white">Suspended</Badge> : <Badge className="bg-green-600 text-white">Active</Badge>}</td>
                          <td className="p-4">
                            {u.role !== 'super_admin' && (
                              <div className="flex gap-2">
                                <Button variant="outline" size="sm" onClick={() => handleSuspend(u.id, u.is_suspended)} className="border-slate-600 text-slate-300 hover:bg-slate-700">
                                  {u.is_suspended ? <UserCheck className="h-4 w-4" /> : <UserX className="h-4 w-4" />}
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => { setPlanDialogUser(u); setSelectedPlan(u.subscription?.plan || 'starter'); setSelectedCycle(u.subscription?.billing_cycle || 'monthly'); setPlanDialogOpen(true); }} className="border-slate-600 text-slate-300 hover:bg-slate-700">
                                  <Crown className="h-4 w-4" />
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => { setDeleteDialogUser(u); setDeleteDialogOpen(true); }} className="border-red-600 text-red-400 hover:bg-red-900/50">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
            {userPages > 1 && (
              <div className="flex justify-center gap-2">
                <Button variant="outline" size="sm" disabled={userPage === 1} onClick={() => setUserPage(p => p - 1)} className="border-slate-600 text-slate-300 hover:bg-slate-700"><ChevronLeft className="h-4 w-4" /></Button>
                <span className="py-2 px-3 text-sm text-slate-400">Page {userPage} of {userPages}</span>
                <Button variant="outline" size="sm" disabled={userPage === userPages} onClick={() => setUserPage(p => p + 1)} className="border-slate-600 text-slate-300 hover:bg-slate-700"><ChevronRight className="h-4 w-4" /></Button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'analytics' && (
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader><CardTitle className="text-white">Analytics Dashboard</CardTitle></CardHeader>
            <CardContent>
              <p className="text-slate-400">Analytics data coming soon. View the Overview tab for current metrics.</p>
            </CardContent>
          </Card>
        )}

        {activeTab === 'audit-logs' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="flex gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-slate-400" />
                <select
                  value={auditFilter.resource_type}
                  onChange={e => { setAuditFilter(f => ({ ...f, resource_type: e.target.value })); setAuditPage(1); }}
                  className="bg-slate-800 border border-slate-600 text-white rounded-md px-3 py-2 text-sm"
                  data-testid="audit-resource-filter"
                >
                  <option value="">All Resources</option>
                  <option value="invoice">Invoice</option>
                  <option value="reservation">Reservation</option>
                  <option value="payment">Payment</option>
                  <option value="staff">Staff</option>
                  <option value="user">User</option>
                </select>
              </div>
              <select
                value={auditFilter.action}
                onChange={e => { setAuditFilter(f => ({ ...f, action: e.target.value })); setAuditPage(1); }}
                className="bg-slate-800 border border-slate-600 text-white rounded-md px-3 py-2 text-sm"
                data-testid="audit-action-filter"
              >
                <option value="">All Actions</option>
                <option value="CREATE">Create</option>
                <option value="UPDATE">Update</option>
                <option value="DELETE">Delete</option>
                <option value="LOGIN">Login</option>
              </select>
              <span className="text-slate-400 text-sm self-center">{auditTotal} total entries</span>
            </div>

            <Card className="bg-slate-800 border-slate-700">
              {auditLoading ? (
                <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" /></div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="audit-logs-table">
                    <thead className="bg-slate-900">
                      <tr>
                        <th className="text-left p-4 font-medium text-slate-300">Timestamp</th>
                        <th className="text-left p-4 font-medium text-slate-300">Action</th>
                        <th className="text-left p-4 font-medium text-slate-300">Resource</th>
                        <th className="text-left p-4 font-medium text-slate-300">User</th>
                        <th className="text-left p-4 font-medium text-slate-300">IP</th>
                        <th className="text-left p-4 font-medium text-slate-300">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditLogs.length === 0 ? (
                        <tr><td colSpan={6} className="text-center py-8 text-slate-500">No audit logs found</td></tr>
                      ) : (
                        auditLogs.map(log => (
                          <tr key={log.id} className="border-t border-slate-700 hover:bg-slate-750">
                            <td className="p-4 text-xs text-slate-400 whitespace-nowrap">{new Date(log.created_at).toLocaleString()}</td>
                            <td className="p-4">
                              <Badge className={
                                log.action === 'DELETE' ? 'bg-red-600 text-white' :
                                log.action === 'CREATE' ? 'bg-green-600 text-white' :
                                log.action === 'UPDATE' ? 'bg-blue-600 text-white' :
                                'bg-slate-600 text-slate-200'
                              }>{log.action}</Badge>
                            </td>
                            <td className="p-4 text-sm text-slate-300">{log.resource_type}</td>
                            <td className="p-4"><div className="text-sm text-slate-300">{log.user_email}</div></td>
                            <td className="p-4 text-xs text-slate-500">{log.ip_address}</td>
                            <td className="p-4 text-sm text-slate-400 max-w-xs truncate">{log.description}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {auditTotal > 20 && (
              <div className="flex justify-center gap-2">
                <Button variant="outline" size="sm" disabled={auditPage === 1} onClick={() => setAuditPage(p => p - 1)} className="border-slate-600 text-slate-300 hover:bg-slate-700"><ChevronLeft className="h-4 w-4" /></Button>
                <span className="py-2 px-3 text-sm text-slate-400">Page {auditPage} of {Math.ceil(auditTotal / 20)}</span>
                <Button variant="outline" size="sm" disabled={auditPage >= Math.ceil(auditTotal / 20)} onClick={() => setAuditPage(p => p + 1)} className="border-slate-600 text-slate-300 hover:bg-slate-700"><ChevronRight className="h-4 w-4" /></Button>
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog open={planDialogOpen} onOpenChange={setPlanDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader><DialogTitle className="text-white">Update Plan</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-400">User: <strong className="text-white">{planDialogUser?.email}</strong></p>
            <div>
              <label className="text-sm font-medium text-slate-300">Plan</label>
              <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="starter">Starter</SelectItem>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="premium">Premium</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300">Billing</label>
              <Select value={selectedCycle} onValueChange={setSelectedCycle}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanDialogOpen(false)} className="border-slate-600 text-slate-300 hover:bg-slate-700">Cancel</Button>
            <Button onClick={handlePlanUpdate} className="bg-blue-600 hover:bg-blue-700">Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader><DialogTitle className="text-white">Delete User</DialogTitle></DialogHeader>
          <div className="py-4">
            <p className="text-slate-300">Are you sure you want to delete this user?</p>
            <p className="text-white font-semibold mt-2">{deleteDialogUser?.email}</p>
            <div className="mt-4 p-3 bg-red-900/30 border border-red-700 rounded-lg">
              <p className="text-red-400 text-sm">This action is permanent and will delete:</p>
              <ul className="text-red-300 text-sm mt-2 list-disc list-inside">
                <li>User account</li>
                <li>Hotel and all rooms</li>
                <li>All reservations and guest data</li>
                <li>Billing and expense records</li>
                <li>Staff accounts</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteDialogOpen(false); setDeleteDialogUser(null); }} className="border-slate-600 text-slate-300 hover:bg-slate-700">Cancel</Button>
            <Button onClick={handleDeleteUser} disabled={deleting} className="bg-red-600 hover:bg-red-700">
              {deleting ? 'Deleting...' : 'Delete User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Hotel Dialog */}
      <Dialog open={editHotelDialogOpen} onOpenChange={setEditHotelDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader><DialogTitle className="text-white">Edit Hotel</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-slate-300">Hotel Name</label>
              <Input 
                value={editHotelData.name} 
                onChange={e => setEditHotelData(d => ({ ...d, name: e.target.value }))} 
                className="bg-slate-700 border-slate-600 text-white mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300">City</label>
              <Input 
                value={editHotelData.city} 
                onChange={e => setEditHotelData(d => ({ ...d, city: e.target.value }))} 
                className="bg-slate-700 border-slate-600 text-white mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300">Country</label>
              <Input 
                value={editHotelData.country} 
                onChange={e => setEditHotelData(d => ({ ...d, country: e.target.value }))} 
                className="bg-slate-700 border-slate-600 text-white mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300">Address</label>
              <Input 
                value={editHotelData.address} 
                onChange={e => setEditHotelData(d => ({ ...d, address: e.target.value }))} 
                className="bg-slate-700 border-slate-600 text-white mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditHotelDialogOpen(false)} className="border-slate-600 text-slate-300 hover:bg-slate-700">Cancel</Button>
            <Button onClick={handleEditHotel} className="bg-blue-600 hover:bg-blue-700">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Hotel Dialog */}
      <Dialog open={deleteHotelDialogOpen} onOpenChange={setDeleteHotelDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader><DialogTitle className="text-white">Delete Hotel</DialogTitle></DialogHeader>
          <div className="py-4">
            <p className="text-slate-300">Are you sure you want to delete this hotel?</p>
            <p className="text-white font-semibold mt-2">{selectedHotel?.name}</p>
            <div className="mt-4 p-3 bg-red-900/30 border border-red-700 rounded-lg">
              <p className="text-red-400 text-sm">This action is permanent and will delete:</p>
              <ul className="text-red-300 text-sm mt-2 list-disc list-inside">
                <li>Hotel and all rooms ({selectedHotel?.stats?.rooms || 0} rooms)</li>
                <li>All room types</li>
                <li>All reservations</li>
                <li>All guest records</li>
                <li>Billing and expense records</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteHotelDialogOpen(false); setSelectedHotel(null); }} className="border-slate-600 text-slate-300 hover:bg-slate-700">Cancel</Button>
            <Button onClick={handleDeleteHotel} disabled={hotelDeleting} className="bg-red-600 hover:bg-red-700">
              {hotelDeleting ? 'Deleting...' : 'Delete Hotel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
